import React, { useState, useEffect } from "react";
import { toast } from "react-toastify";
import { useTranslation } from "react-i18next";
import userService from "../../services/userService";
import productService from "../../services/productService";
import pdfIcon from "./pdfIcon.png";
import stepIcon from "./stepIcon.png";
import { formatKatalogKodu } from "../../utils/formatters";
import { IconDownload, IconCalendar, IconFileDescription, IconInbox } from "@tabler/icons-react";
import { variables } from "../../config/Variables";

export const DownloadedProducts = () => {
    const { t } = useTranslation();
    const [downloadedFiles, setDownloadedFiles] = useState([]);
    const [loading, setLoading] = useState(false);
    const [downloadingId, setDownloadingId] = useState(null);

    useEffect(() => {
        const userKey = sessionStorage.getItem("activationKey");
        if (userKey) {
            fetchDownloadedFiles(userKey);
        } else {
            toast.error(t('mesajOturumZamanAsimi', 'Oturum zaman aşımına uğradı.'));
        }
    }, [t]);

    const fetchDownloadedFiles = async (userId) => {
        setLoading(true);
        try {
            const data = await userService.getDownloadedFiles(userId);
            setDownloadedFiles(data || []);
        } catch (error) {
            toast.error(t('mesajSunucuTarafiHatasi', 'Sunucu tarafında bir hata oluştu.'));
        } finally {
            setLoading(false);
        }
    };

    const handleDownload = async (file, index, type) => {
        const fileName = file.DosyaAdi;
        const downloadKey = `${index}-${type}`;
        
        setDownloadingId(downloadKey);
        toast.info(type === 'pdf' ? t('mesajPdfIndirmeBasladi', 'PDF indirmesi başladı...') : t('mesajStepIndirmeBasladi', 'STEP indirmesi başladı...'));

        try {
            const data = await productService.downloadFile(
                fileName, 
                sessionStorage.getItem("activationKey"), 
                file.Id || 0, 
                type
            );
            const url = window.URL.createObjectURL(new Blob([data]));
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', fileName);
            document.body.appendChild(link);
            link.click();
            link.remove();
        } catch (error) {
            console.error("Download failed", error);
            toast.error(t('mesajDosyaIndirmeHatasi', 'Dosya indirme hatası.'));
        } finally {
            setDownloadingId(null);
        }
    };

    const thStyle = {
        fontSize: '11px',
        fontWeight: 700,
        textTransform: 'uppercase',
        letterSpacing: '0.5px',
        color: '#64748b',
        padding: '12px 16px',
        borderBottom: '2px solid #e2e8f0',
        whiteSpace: 'nowrap',
        background: '#f8fafc',
    };

    const actionBtn = (bg, color) => ({
        background: bg,
        border: 'none',
        borderRadius: '8px',
        padding: '6px 10px',
        cursor: 'pointer',
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        lineHeight: 1,
        color,
        transition: 'all 0.15s ease',
    });

    return (
        <div className="container-fluid py-4" style={{ maxWidth: '1280px' }}>

            {/* Header Banner */}
            <div className="rounded-4 mb-4 p-4 d-flex align-items-center justify-content-between text-white shadow-sm"
                style={{ background: 'linear-gradient(135deg, #1e2a38 0%, #2563eb 100%)' }}>
                <div className="d-flex align-items-center gap-3">
                    <div style={{
                        width: 52, height: 52, borderRadius: '16px',
                        background: 'rgba(255,255,255,0.15)',
                        display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
                    }}>
                        <IconDownload size={28} color="#fff" />
                    </div>
                    <div>
                        <h4 className="fw-bold mb-1">{t('menuIndirdigimUrunler', 'İndirdiğim Ürünler')}</h4>
                        <p className="m-0 text-white-50" style={{ fontSize: '13px' }}>
                            {t('IndirdigimUrunlerAltBaslik', 'Geçmişte indirdiğiniz PDF ve CAD model dosyalarınızın listesi.')}
                        </p>
                    </div>
                </div>
                {!loading && downloadedFiles.length > 0 && (
                    <span className="badge bg-white text-dark rounded-pill px-3 py-2 fw-bold" style={{ fontSize: '13px' }}>
                        {downloadedFiles.length} {t('mesajUrunBulundu', 'ürün')}
                    </span>
                )}
            </div>

            {/* Table / Loading Card */}
            {loading ? (
                <div className="card border-0 shadow-sm rounded-4 p-5 text-center">
                    <div className="spinner-border text-primary mx-auto mb-3" role="status">
                        <span className="visually-hidden">Loading...</span>
                    </div>
                    <p className="text-muted m-0">{t('mesajYukleniyor', 'İndirilen ürünler yükleniyor...')}</p>
                </div>
            ) : (
                <div className="card border-0 shadow-sm rounded-4 overflow-hidden" style={{ border: '1px solid #e2e8f0' }}>
                    <div className="table-responsive">
                        <table className="table table-hover mb-0 align-middle">
                            <thead>
                                <tr>
                                    <th style={{ ...thStyle, width: '70px', textAlign: 'center' }}>{t('Gorsel', 'Görsel')}</th>
                                    <th style={{ ...thStyle, minWidth: '160px' }}>{t('tabloBaslikUrunKodu', 'Ürün Kodu')}</th>
                                    <th style={{ ...thStyle, minWidth: '180px' }}>{t('tabloBaslikKatalogKodu', 'Katalog Kodu')}</th>
                                    <th style={{ ...thStyle, minWidth: '220px' }}>{t('tabloBaslikDosyaAdi', 'İndirilen Dosya')}</th>
                                    <th style={{ ...thStyle, textAlign: 'center', minWidth: '140px' }}>
                                        <IconCalendar size={14} style={{ marginBottom: 2, marginRight: 4 }} />
                                        {t('tabloBaslikIndirmeTarihi', 'İndirme Tarihi')}
                                    </th>
                                </tr>
                            </thead>
                            <tbody>
                                {downloadedFiles && downloadedFiles.length > 0 ? (
                                    downloadedFiles.map((file, index) => {
                                        const fileNameLower = (file.DosyaAdi || "").toLowerCase();
                                        const isPdf = fileNameLower.endsWith(".pdf");
                                        const isStep = fileNameLower.endsWith(".step") || fileNameLower.endsWith(".stp");
                                        const fileType = isPdf ? "pdf" : (isStep ? "step" : "");
                                        const downloadKey = `${index}-${fileType}`;

                                        return (
                                            <tr key={index}>
                                                {/* Görsel */}
                                                <td style={{ padding: '10px 16px', textAlign: 'center' }}>
                                                    <div 
                                                        className="rounded-3 border bg-light d-inline-flex align-items-center justify-content-center"
                                                        style={{ width: '52px', height: '52px', overflow: 'hidden' }}
                                                    >
                                                        {file.ImageFile ? (
                                                            <img
                                                                src={variables.IMAGE_URL + file.ImageFile}
                                                                alt={file.OrjinalKodu || ""}
                                                                style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }}
                                                                onError={(e) => {
                                                                    e.target.style.display = 'none';
                                                                    if (e.target.parentNode) {
                                                                        e.target.parentNode.innerHTML = '<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#94a3b8" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path><polyline points="14 2 14 8 20 8"></polyline><line x1="16" y1="13" x2="8" y2="13"></line><line x1="16" y1="17" x2="8" y2="17"></line><polyline points="10 9 9 9 8 9"></polyline></svg>';
                                                                    }
                                                                }}
                                                            />
                                                        ) : (
                                                            <IconFileDescription size={24} color="#94a3b8" />
                                                        )}
                                                    </div>
                                                </td>

                                                {/* Ürün Kodu */}
                                                <td style={{ padding: '12px 16px' }}>
                                                    <span style={{ fontWeight: 700, color: '#0f172a', fontSize: '14px' }}>
                                                        {file.OrjinalKodu}
                                                    </span>
                                                </td>

                                                {/* Katalog Kodu */}
                                                <td style={{ padding: '12px 16px' }}>
                                                    {file.KatalogKodu ? (
                                                        <span 
                                                            className="badge" 
                                                            style={{ backgroundColor: '#1e293b', color: '#ffffff', fontSize: '13px', fontWeight: 700, padding: '5px 12px', borderRadius: '7px', letterSpacing: '0.2px' }}
                                                        >
                                                            {formatKatalogKodu(file.KatalogKodu)}
                                                        </span>
                                                    ) : (
                                                        <span className="text-muted" style={{ fontSize: '13px' }}>-</span>
                                                    )}
                                                </td>

                                                {/* İndirilen Dosya */}
                                                <td style={{ padding: '12px 16px' }}>
                                                    <div className="d-flex align-items-center justify-content-between gap-3 bg-light p-2 rounded-3 border" style={{ borderColor: '#e2e8f0' }}>
                                                        <span className="text-truncate fw-medium" style={{ fontSize: '12px', color: '#334155', maxWidth: '160px' }} title={file.DosyaAdi}>
                                                            {file.DosyaAdi}
                                                        </span>
                                                        {fileType && (
                                                            <button
                                                                style={isPdf ? actionBtn('#fef2f2', '#dc2626') : actionBtn('#eff6ff', '#2563a8')}
                                                                onClick={() => handleDownload(file, index, fileType)}
                                                                disabled={downloadingId !== null}
                                                                title={isPdf ? "PDF İndir" : "STEP İndir"}
                                                            >
                                                                {downloadingId === downloadKey ? (
                                                                    <div className="spinner-border spinner-border-sm" role="status"
                                                                        style={{ width: 18, height: 18, color: isPdf ? '#dc2626' : '#2563a8' }} />
                                                                ) : (
                                                                    <img
                                                                        width={28}
                                                                        src={isPdf ? pdfIcon : stepIcon}
                                                                        alt={isPdf ? "PDF" : "STEP"}
                                                                        style={{ opacity: downloadingId !== null && downloadingId !== downloadKey ? 0.4 : 1 }}
                                                                    />
                                                                )}
                                                            </button>
                                                        )}
                                                    </div>
                                                </td>

                                                {/* İndirme Tarihi */}
                                                <td style={{ padding: '12px 16px', textAlign: 'center' }}>
                                                    <span className="badge bg-light text-slate-700 border" style={{ color: '#475569', fontSize: '12px', fontWeight: 500, padding: '5px 10px', borderRadius: '6px' }}>
                                                        {file.IndirmeTarihi || "-"}
                                                    </span>
                                                </td>
                                            </tr>
                                        );
                                    })
                                ) : (
                                    <tr>
                                        <td colSpan="5" style={{ padding: '64px 16px', textAlign: 'center', color: '#94a3b8' }}>
                                            <IconInbox size={40} style={{ marginBottom: 12, opacity: 0.4 }} />
                                            <h6 className="fw-bold text-slate-700 mb-1">{t('mesajHenuzDosyaIndirilmedi', 'Henüz indirilmiş bir dosyanız bulunmuyor')}</h6>
                                            <small className="text-muted">{t('mesajIndirdiginizDosyalarBurada', 'Ürün sayfalarından indirdiğiniz CAD ve PDF dosyaları bu listede görüntülenecektir.')}</small>
                                        </td>
                                    </tr>
                                )}
                            </tbody>
                        </table>
                    </div>
                </div>
            )}
        </div>
    );
};

export default DownloadedProducts;